<template>
  <Drawer
    :aria-label="`Delivery of ${delivery.event_type}`"
    no-footer
    data-cy="webhook-delivery-drawer"
    @hide-drawer="emit('close')"
  >
    <template #title>
      <div class="pr-10">
        <div class="mb-1 flex flex-wrap items-center gap-2">
          <h2 class="m-0 text-base font-semibold text-gray-900">
            <code class="text-sm">{{ current.event_type }}</code>
          </h2>
          <span
            class="whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold"
            :class="deliveryStatusStyle(current.status).pill"
            data-cy="webhook-delivery-drawer-status"
            >{{ deliveryStatusStyle(current.status).label }}</span
          >
          <span
            v-if="isTestDelivery(current)"
            class="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-600"
            data-cy="webhook-delivery-test"
            >Test</span
          >
        </div>
        <p class="m-0 text-sm text-gray-600">
          To {{ deliveryEndpointLabel(current) }}
          <span v-if="current.endpoint.archived" class="text-gray-500"
            >(archived)</span
          >
          · {{ formatDateTimeWithTimezone(current.created_at) }}
        </p>
      </div>
    </template>

    <div class="space-y-5 px-4 pb-6">
      <div
        class="rounded-lg border p-3 text-sm"
        :class="
          current.status === 'failed'
            ? 'border-red-200 bg-red-50'
            : 'border-gray-200 bg-gray-50'
        "
        data-cy="webhook-delivery-outcome"
      >
        <p class="m-0 font-medium text-gray-900">{{ current.outcome.title }}</p>
        <p v-if="current.outcome.hint" class="m-0 mt-1 text-gray-700">
          {{ current.outcome.hint }}
        </p>
        <p
          v-if="current.next_attempt_at"
          class="m-0 mt-1 text-xs text-gray-600"
          data-cy="webhook-delivery-next-attempt"
        >
          Next attempt {{ formatDateTimeWithTimezone(current.next_attempt_at) }}
        </p>
      </div>

      <div v-if="canRetry(current.status) && !readOnly">
        <button
          type="button"
          class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
          :disabled="retrying"
          data-cy="webhook-delivery-retry"
          @click="retry"
        >
          {{ retrying ? "Retrying…" : "Retry" }}
        </button>
        <p class="m-0 mt-1 text-xs text-gray-500">
          Sends the same body with the same webhook-id, so your endpoint can
          tell it's the same event. It starts a fresh set of up to 8 attempts
          over about 27 hours.
        </p>
      </div>

      <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        <dt class="text-gray-500">Delivery id</dt>
        <dd class="m-0 break-all font-mono" data-cy="webhook-delivery-id">
          {{ current.id }}
        </dd>
        <dt class="text-gray-500">Event id</dt>
        <dd class="m-0 break-all font-mono">{{ current.event_id }}</dd>
        <dt class="text-gray-500">Event happened</dt>
        <dd class="m-0">
          {{ formatDateTimeWithTimezone(current.occurred_at) }}
        </dd>
      </dl>

      <div v-if="loading" class="flex justify-center py-6">
        <Spinner size="small" />
      </div>

      <Alert v-else-if="loadFailed" type="error">
        The request and its attempts couldn't be loaded.
        <button type="button" class="link ml-1" @click="load">Try again</button>
      </Alert>

      <template v-else-if="detail">
        <section data-cy="webhook-delivery-headers">
          <h3 class="mb-1 text-xs font-semibold uppercase text-gray-500">
            Request headers
          </h3>
          <table
            class="w-full rounded border border-gray-200 text-left font-mono text-xs"
          >
            <tbody>
              <tr
                v-for="(value, name) in detail.request.headers"
                :key="name"
                class="border-b border-gray-100 last:border-b-0"
              >
                <th class="whitespace-nowrap px-2 py-1 font-semibold">
                  {{ name }}
                </th>
                <td class="break-all px-2 py-1">{{ value ?? "—" }}</td>
              </tr>
            </tbody>
          </table>
          <p class="m-0 mt-1 text-xs text-gray-500">
            The signature is hidden. Each attempt is signed with a fresh
            timestamp.
          </p>
        </section>

        <JsonViewer
          :value="detail.request.body"
          title="Request body"
          empty-text="No body was recorded."
        />

        <section data-cy="webhook-delivery-attempts">
          <h3 class="mb-2 text-xs font-semibold uppercase text-gray-500">
            Attempts
          </h3>
          <p v-if="!detail.attempts.length" class="m-0 text-sm text-gray-500">
            No attempts yet.
          </p>
          <ol
            v-else
            class="m-0 list-none space-y-3 border-l border-gray-200 p-0 pl-4"
          >
            <li
              v-for="attempt in detail.attempts"
              :key="attempt.number"
              class="relative"
              :data-cy="`webhook-delivery-attempt:${attempt.number}`"
            >
              <span
                class="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full"
                :class="
                  attempt.outcome.category === 'success'
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
              ></span>
              <div class="flex flex-wrap items-baseline gap-2 text-sm">
                <span class="font-medium text-gray-900"
                  >Attempt {{ attempt.number }}</span
                >
                <span class="text-xs text-gray-500">{{
                  formatDateTimeWithTimezone(attempt.started_at)
                }}</span>
                <span
                  class="rounded bg-gray-100 px-1.5 text-xs font-semibold text-gray-700"
                  >{{ attempt.response_code ?? "No response" }}</span
                >
              </div>
              <p class="m-0 text-sm text-gray-700">
                {{ attempt.outcome.title }}
              </p>
              <pre
                v-if="attempt.response_excerpt"
                class="m-0 mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded border border-gray-200 bg-white p-2 font-mono text-xs"
                data-cy="webhook-delivery-response"
                >{{ attempt.response_excerpt }}</pre>
            </li>
          </ol>
        </section>
      </template>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { formatDateTimeWithTimezone } from "@/lib/helpers";

import JsonViewer from "@/components/JsonViewer.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import type {
  WebhookDelivery,
  WebhookDeliveryDetail
} from "@/vuex/modules/integrationWebhooks";
import {
  apiError,
  canRetry,
  deliveryEndpointLabel,
  deliveryStatusStyle,
  isTestDelivery
} from "./webhooksHub";

const props = withDefaults(
  defineProps<{ delivery: WebhookDelivery; readOnly?: boolean }>(),
  { readOnly: false }
);
const emit = defineEmits<{ close: []; changed: [delivery: WebhookDelivery] }>();

const store = useStore();

const detail = ref<WebhookDeliveryDetail | null>(null);
const loading = ref(false);
const loadFailed = ref(false);
const retrying = ref(false);

// The row until the detail arrives, then the detail.
const current = computed<WebhookDelivery>(() => detail.value ?? props.delivery);

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    detail.value = await store.dispatch(
      "fetchWebhookDelivery",
      props.delivery.id
    );
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

async function retry() {
  retrying.value = true;
  try {
    const updated: WebhookDelivery = await store.dispatch(
      "retryWebhookDelivery",
      current.value.id
    );
    window.notify.success("Retry queued.");
    emit("changed", updated);
    await load();
  } catch (error) {
    window.notify.error(
      apiError(error, "The delivery couldn't be retried. Try again.").message
    );
  } finally {
    retrying.value = false;
  }
}

onMounted(load);
</script>
